import { createStore, createEvent, createEffect, combine, sample } from 'effector';
import { authApi } from '../api/auth';
import {
  resetUnauthorizedRedirectState,
  SESSION_UNAUTHORIZED_EVENT,
} from '../api/index';
import { User } from '../api/types';
import { userHasRole } from '../config/roles';
import {
  $activeTenant,
  $activeTenantAccessLevel,
  clearTenantContext,
  fetchAdminTenantsFx,
  fetchContractorTenantsFx,
} from './tenantContext';

// Stores
export const $isAuthenticated = createStore(false);
export const $user = createStore<User | null>(null);
export const $authLoading = createStore(true); // Initial loading state
export const $canDeleteComplex = combine($user, $activeTenantAccessLevel, (u, access) => {
  if (!u) return false;
  if (userHasRole(u, 'superadmin') || userHasRole(u, 'admin')) return true;
  if (userHasRole(u, 'contractor') && access === 'write') return true;
  return false;
});

export const $canEditComplex = combine($user, $activeTenantAccessLevel, (u, access) => {
  if (!u) return false;
  if (userHasRole(u, 'superadmin') || userHasRole(u, 'admin')) return true;
  if (userHasRole(u, 'contractor') && access === 'write') return true;
  return false;
});

export const $canManageDevices = combine($user, $activeTenantAccessLevel, (u, access) => {
  if (!u) return false;
  if (
    userHasRole(u, 'superadmin') ||
    userHasRole(u, 'admin') ||
    userHasRole(u, 'manager')
  ) return true;
  return userHasRole(u, 'contractor') && access === 'write';
});

export const $canEditTickets = combine($user, $activeTenantAccessLevel, (u, access) => {
  if (!u) return false;
  if (
    userHasRole(u, 'superadmin') ||
    userHasRole(u, 'admin') ||
    userHasRole(u, 'manager')
  ) return true;
  return userHasRole(u, 'contractor') && access === 'write';
});

/** mdu | settlement — режим клиента (посёлок / МКД) */
export const $objectKind = combine($user, $activeTenant, (user, activeTenant) => {
  const fromTenant = String(activeTenant?.object_kind || '').trim().toLowerCase();
  if (fromTenant === 'settlement' || fromTenant === 'mdu') return fromTenant;
  const fromUser = String(user?.object_kind || '').trim().toLowerCase();
  if (fromUser === 'settlement' || fromUser === 'mdu') return fromUser;
  return 'mdu';
});

export const $isSettlementTenant = $objectKind.map((kind) => kind === 'settlement');

export const $tenantDataStartDate = combine($user, $activeTenant, (user, activeTenant) => (
  activeTenant?.data_available_from || user?.data_available_from || null
));

// Events
export const loginFx = createEffect(authApi.login); // логин будет устанавливать куку
export const logoutFx = createEffect(authApi.logout); // logout должен удалить куку на сервере
export const fetchCurrentUserFx = createEffect(authApi.getCurrentUser);
export const sessionUnauthorized = createEvent();

if (typeof window !== 'undefined') {
  window.addEventListener(SESSION_UNAUTHORIZED_EVENT, () => sessionUnauthorized());
}

// Reducers
$isAuthenticated
  .on(loginFx.done, () => true)
  .on(fetchCurrentUserFx.doneData, () => true)
  .on(logoutFx.done, () => false)
  .on(fetchCurrentUserFx.fail, () => false)
  .on(loginFx.fail, () => false)
  .on(sessionUnauthorized, () => false);

$user
  .on(fetchCurrentUserFx.doneData, (_, user) => user)
  .on(logoutFx.done, () => null)
  .on(sessionUnauthorized, () => null)
  .on(loginFx.done, () => null); // после логина пока юзер не загружен

sample({
  clock: fetchCurrentUserFx.doneData,
  filter: (user) => userHasRole(user, 'contractor'),
  target: fetchContractorTenantsFx,
});

sample({
  clock: fetchCurrentUserFx.doneData,
  filter: (user) => userHasRole(user, 'superadmin'),
  target: fetchAdminTenantsFx,
});

sample({
  clock: fetchCurrentUserFx.doneData,
  filter: (user) =>
    !userHasRole(user, 'contractor') && !userHasRole(user, 'superadmin'),
  target: clearTenantContext,
});

sample({
  clock: loginFx.done,
  target: clearTenantContext,
});

sample({
  clock: logoutFx.done,
  target: clearTenantContext,
});

sample({
  clock: fetchCurrentUserFx.fail,
  target: clearTenantContext,
});

sample({
  clock: sessionUnauthorized,
  target: clearTenantContext,
});

// Side effects
// Note: We'll handle navigation in the component to avoid full page reload
// and to properly use React Router's navigation

// Handle loading state
$authLoading
  .on(fetchCurrentUserFx, () => true)
  .on(fetchCurrentUserFx.done, () => false)
  .on(fetchCurrentUserFx.fail, () => false)
  .on(sessionUnauthorized, () => false);

const resetUnauthorizedRedirect = () => {
  resetUnauthorizedRedirectState();
};

loginFx.done.watch(resetUnauthorizedRedirect);
fetchCurrentUserFx.done.watch(resetUnauthorizedRedirect);
logoutFx.done.watch(resetUnauthorizedRedirect);

// Инициализация
fetchCurrentUserFx();
